import { FC, memo, useEffect, useState } from 'react';
import { UserProvider } from './context/userContext';
import { useUserContext } from './hook/useUserContext';
import { Box, Chip } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataTable, TableColumn, TableAction, FilterConfig } from '../../common/table';
import EditModal from './component/UserEditModal';


interface IUserPage extends Partial<Record<'a' | 'b', string>> {}

const UsersPage: FC<IUserPage> = () => {
    const { users, getUsers, chooseUserForEdit, deleteExistingUser } = useUserContext();
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        try {
            await getUsers(0, 1000);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (row: any): void => {
        chooseUserForEdit(row.id);
        setEditModalOpen(true);
    };

    const handleDeleteClick = (row: any): void => {
        if (window.confirm(`Are you sure you want to delete user "${row.firstName} ${row.lastName}"?`)) {
            deleteExistingUser(row.id);
        }
    };

    const handleEditModalClose = (): void => {
        chooseUserForEdit();
        setEditModalOpen(false);
    };

    const filters: FilterConfig[] = [
        {
            field: 'role',
            label: 'Role',
            options: [
                { label: 'Admin', value: 'ROLE_ADMIN' },
                { label: 'Operator Supervisor', value: 'ROLE_OPERATOR_SUPERVISOR' },
                { label: 'Storekeeper', value: 'ROLE_STOREKEEPER' },
                { label: 'Operator', value: 'ROLE_OPERATOR' },
                { label: 'Client', value: 'ROLE_CLIENT' },
            ],
        },
        {
            field: 'isActive',
            label: 'Status',
            options: [
                { label: 'Active', value: 'true' },
                { label: 'Inactive', value: 'false' },
            ],
        },
    ];

    const columns: TableColumn[] = [
        {
            field: 'firstName',
            headerName: 'First Name',
            width: 150,
            sortable: true,
        },
        {
            field: 'lastName',
            headerName: 'Last Name',
            width: 150,
            sortable: true,
        },
        {
            field: 'mail',
            headerName: 'Email',
            flex: 1,
            minWidth: 200,
            sortable: true,
        },
        {
            field: 'phoneNumber',
            headerName: 'Phone',
            width: 180,
            sortable: true,
        },
        {
            field: 'role',
            headerName: 'Role',
            width: 180,
            sortable: true,
            renderCell: (value) => {
                const roleColors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
                    ROLE_ADMIN: 'error',
                    ROLE_OPERATOR_SUPERVISOR: 'warning',
                    ROLE_STOREKEEPER: 'info',
                    ROLE_OPERATOR: 'secondary',
                    ROLE_CLIENT: 'primary',
                };
                const color = roleColors[value] || 'default';
                const label = value?.replace('ROLE_', '').replace('_', ' ');

                return (
                    <Chip
                        label={label}
                        color={color}
                        size="small"
                        sx={{ fontWeight: 500 }}
                    />
                );
            },
        },
        {
            field: 'createAt',
            headerName: 'Registered',
            width: 150,
            sortable: true,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
        },
    ];

    const actions: TableAction[] = [
        {
            label: 'Edit',
            icon: <EditIcon />,
            color: 'primary',
            variant: 'outlined',
            onClick: handleEditClick,
            requirePermission: {
                resource: 'users',
                action: 'edit',
            },
        },
        {
            label: 'Delete',
            icon: <DeleteIcon />,
            color: 'error',
            variant: 'outlined',
            onClick: handleDeleteClick,
            requirePermission: {
                resource: 'users',
                action: 'delete',
            },
        },
    ];

    return (
        <Box className="iauto-admin-page-wrapper" >
            <DataTable
                title="Users"
                icon={<PeopleIcon />}
                columns={columns}
                data={users?.items || []}
                actions={actions}
                loading={loading}
                emptyMessage="No Users Found"
                emptyDescription="There are no users in the system yet. Use the 'Create User' option from the menu."
                pagination={true}
                pageSize={50}
                sortable={true}
                stickyHeader={true}
                maxHeight={700}
                searchable={true}
                searchPlaceholder="Search by name, email, or phone..."
                filters={filters}
            />
            <EditModal
                open={editModalOpen}
                onClose={handleEditModalClose}
            />
        </Box>
    );
};

const UsersProviderHOC: FC<IUserPage> = memo((props: IUserPage) => {
    return (
        <UserProvider>
            <UsersPage {...props} />
        </UserProvider>
    );
});

export default UsersProviderHOC;
